import React, { useState, useEffect } from "react";
import TopBar from "../globalComponents/topbar";
import Footer from "../globalComponents/footer";
import { useNavigate } from "react-router-dom";
import {
  Box, Typography, Button, Stack, Divider, Card, CircularProgress,
  Chip, LinearProgress, Drawer, IconButton, useTheme, useMediaQuery,
  alpha, Paper,
} from "@mui/material";
import {
  PlayCircle, ArrowForwardIos, MenuBook, EmojiEvents,
  AccessTime, FilterList, Close, CheckCircle,
  TimelapseOutlined, RadioButtonUnchecked, School,
} from "@mui/icons-material";

// ── Brand palette ────────────────────────────────────────────────────────────
const C = {
  primary: "#0A2E8A",
  primaryDark: "#061E5C",
  primaryLight: "#3B5BC9",
  primarySoft: "#E8EDFC",
  primarySofter: "#F2F5FE",
  secondary: "#10682A",
  secondaryDark: "#0A4A1C",
  secondaryLight: "#2E9148",
  secondarySoft: "#E3F2E8",
  secondarySofter: "#F0F8F3",
  bg: "#FAFBFC",
  paper: "#FFFFFF",
  border: "#E5E9F0",
  borderSoft: "#EEF1F6",
  textPrimary: "#0D1117",
  textSecondary: "#5B6B7E",
  textMuted: "#8A9BB0",
  warning: "#D97706",
  warningSoft: "#FEF3C7",
  error: "#DC2626",
  errorSoft: "#FEE2E2",
};

// Difficulty → brand-aligned colors
const DIFFICULTY_COLORS = {
  beginner:     { color: C.secondary, bg: C.secondarySoft },
  intermediate: { color: C.warning,   bg: C.warningSoft },
  advanced:     { color: C.error,     bg: C.errorSoft },
};

// Derive a learner status from progress object
const computeLessonStatus = (progress) => {
  if (!progress) return "not_started";
  const pct = progress.completionPercentage || 0;
  if (pct >= 100 || progress.certificateIssued) return "completed";
  if (pct > 0) return "in_progress";
  return "not_started";
};

const STATUS_CONFIG = {
  not_started: {
    label: "Not Started",
    icon: <RadioButtonUnchecked sx={{ fontSize: 13 }} />,
    color: C.textMuted, bg: C.borderSoft,
    buttonLabel: "Start Learning",
    accent: C.primary,
  },
  in_progress: {
    label: "In Progress",
    icon: <TimelapseOutlined sx={{ fontSize: 13 }} />,
    color: C.primary, bg: C.primarySoft,
    buttonLabel: "Continue Learning",
    accent: C.primary,
  },
  completed: {
    label: "Completed",
    icon: <CheckCircle sx={{ fontSize: 13 }} />,
    color: C.secondary, bg: C.secondarySoft,
    buttonLabel: "Review Lesson",
    accent: C.secondary,
  },
};

export default function Lessons() {
  const [lessons, setLessons] = useState([]);
  const [progressData, setProgressData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterDifficulty, setFilterDifficulty] = useState("all");
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  const lessonsPerPage = 4;
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const user = JSON.parse(localStorage.getItem("user"));
  const learnerId = user?.id || user?._id;
  const firstName = user?.fullName?.split(" ")[0];

  useEffect(() => {
    fetchLessons();
    if (learnerId) fetchProgress();
  }, []);

  const fetchLessons = async () => {
    try {
      const response = await fetch("https://backend-global-1vq9.onrender.com/api/lessons/all?published=true");
      if (!response.ok) throw new Error("Failed to fetch lessons");
      const data = await response.json();
      setLessons(data.data || []);
    } catch (err) {
      setError("Unable to load lessons. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const fetchProgress = async () => {
    try {
      const response = await fetch(`https://backend-global-1vq9.onrender.com/api/lessons/progress/${learnerId}`);
      const data = await response.json();
      const progressMap = {};
      if (data.data) {
        data.data.forEach((prog) => {
          const lessonId = prog.lessonId?._id || prog.lessonId;
          progressMap[lessonId] = prog;
        });
      }
      setProgressData(progressMap);
    } catch (err) {
      console.error("Error fetching progress:", err);
    }
  };

  const categories = ["all", ...new Set(lessons.map((l) => l.category).filter(Boolean))];
  const difficulties = ["all", "beginner", "intermediate", "advanced"];

  const filteredLessons = lessons.filter((lesson) => {
    const categoryMatch = filterCategory === "all" || lesson.category === filterCategory;
    const difficultyMatch = filterDifficulty === "all" || lesson.difficulty === filterDifficulty;
    return categoryMatch && difficultyMatch;
  });

  // ── Sort: in_progress first, then not_started, then completed ──
  const sortedLessons = [...filteredLessons].sort((a, b) => {
    const rank = { in_progress: 0, not_started: 1, completed: 2 };
    return rank[computeLessonStatus(progressData[a._id])] - rank[computeLessonStatus(progressData[b._id])];
  });

  const indexOfLastLesson = currentPage * lessonsPerPage;
  const indexOfFirstLesson = indexOfLastLesson - lessonsPerPage;
  const currentLessons = sortedLessons.slice(indexOfFirstLesson, indexOfLastLesson);
  const totalPages = Math.ceil(sortedLessons.length / lessonsPerPage);

  const nextPage = () => { if (currentPage < totalPages) setCurrentPage((p) => p + 1); };
  const prevPage = () => { if (currentPage > 1) setCurrentPage((p) => p - 1); };

  // ── Overall learner stats ──
  const totalLessons = lessons.length;
  const startedCount = Object.values(progressData).filter((p) => (p.completionPercentage || 0) > 0).length;
  const completedCount = Object.values(progressData).filter(
    (p) => (p.completionPercentage || 0) >= 100 || p.certificateIssued
  ).length;
  const inProgressCount = startedCount - completedCount;

  const filtersActive = filterCategory !== "all" || filterDifficulty !== "all";

  // ── Filter panel ──
  const FilterPanel = () => (
    <Box sx={{ width: "100%", boxSizing: "border-box" }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box sx={{
            width: 4, height: 22,
            background: `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`,
            borderRadius: 1,
          }} />
          <Typography sx={{ color: C.textPrimary, fontSize: 14, fontWeight: 700 }}>
            Filters
          </Typography>
        </Stack>
        {isMobile && (
          <IconButton size="small" onClick={() => setFilterDrawerOpen(false)} sx={{ color: C.textMuted }}>
            <Close fontSize="small" />
          </IconButton>
        )}
      </Stack>

      <Divider sx={{ width: "100%", mb: 2, borderColor: C.borderSoft }} />

      {/* Categories */}
      <Typography sx={{ color: C.textSecondary, fontSize: 12, fontWeight: 700, mb: 1, textTransform: "uppercase", letterSpacing: 0.5 }}>
        Categories
      </Typography>
      <Stack direction="row" spacing={0.75} flexWrap="wrap" gap={0.75} mb={3}>
        {categories.map((cat) => {
          const active = filterCategory === cat;
          return (
            <Button
              key={cat}
              onClick={() => {
                setFilterCategory(cat);
                setCurrentPage(1);
                if (isMobile) setFilterDrawerOpen(false);
              }}
              sx={{
                background: active ? `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})` : C.paper,
                borderRadius: 2,
                border: active ? "none" : `1px solid ${C.border}`,
                color: active ? "#fff" : C.textSecondary,
                textTransform: "capitalize",
                fontSize: 12,
                fontWeight: active ? 700 : 600,
                py: 0.5, px: 1.5,
                boxShadow: "none",
                "&:hover": {
                  background: active ? `linear-gradient(135deg, ${C.primaryDark}, ${C.primary})` : C.primarySofter,
                  borderColor: active ? "transparent" : C.primary,
                },
              }}
            >
              {cat}
            </Button>
          );
        })}
      </Stack>

      {/* Difficulty */}
      <Typography sx={{ color: C.textSecondary, fontSize: 12, fontWeight: 700, mb: 1, textTransform: "uppercase", letterSpacing: 0.5 }}>
        Difficulty
      </Typography>
      <Stack direction="column" spacing={0.75} mb={3}>
        {difficulties.map((diff) => {
          const active = filterDifficulty === diff;
          const dc = DIFFICULTY_COLORS[diff] || { color: C.primary, bg: C.primarySoft };
          return (
            <Button
              key={diff}
              onClick={() => {
                setFilterDifficulty(diff);
                setCurrentPage(1);
                if (isMobile) setFilterDrawerOpen(false);
              }}
              sx={{
                borderRadius: 2,
                border: `1px solid ${active ? dc.color : C.border}`,
                bgcolor: active ? dc.bg : "transparent",
                color: active ? dc.color : C.textSecondary,
                textTransform: "capitalize",
                fontSize: 13,
                fontWeight: active ? 700 : 600,
                justifyContent: "flex-start",
                py: 0.75, px: 1.5,
                "&:hover": { borderColor: dc.color, bgcolor: dc.bg, color: dc.color },
              }}
            >
              {diff}
            </Button>
          );
        })}
      </Stack>

      {/* Clear filters */}
      <Button
        onClick={() => {
          setFilterCategory("all");
          setFilterDifficulty("all");
          setCurrentPage(1);
          if (isMobile) setFilterDrawerOpen(false);
        }}
        disabled={!filtersActive}
        sx={{
          display: "flex", alignItems: "center", justifyContent: "center",
          bgcolor: filtersActive ? C.primarySoft : C.borderSoft,
          borderRadius: 2, py: 1, width: "100%", gap: 0.75,
          textTransform: "none",
          color: filtersActive ? C.primary : C.textMuted,
          fontSize: 12, fontWeight: 700,
          "&:hover": { bgcolor: filtersActive ? alpha(C.primary, 0.15) : C.borderSoft },
        }}
      >
        Clear All Filters
        <ArrowForwardIos sx={{ fontSize: 12 }} />
      </Button>
    </Box>
  );

  // ── Mini stat tile (used in hero stats row) ──
  const StatTile = ({ label, value, accent, accentDark, icon }) => (
    <Paper
      elevation={0}
      sx={{
        position: "relative", flex: 1, minWidth: 0,
        p: { xs: 1.5, sm: 1.75 }, borderRadius: 2.5,
        background: `linear-gradient(135deg, ${accent} 0%, ${accentDark} 100%)`,
        color: "#fff", overflow: "hidden",
        "&::after": {
          content: '""',
          position: "absolute",
          top: "-30%", right: "-15%",
          width: 80, height: 80, borderRadius: "50%",
          background: "rgba(255,255,255,0.08)",
        },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ position: "relative", zIndex: 1 }}>
        <Box sx={{
          width: 30, height: 30, borderRadius: 1.5,
          bgcolor: "rgba(255,255,255,0.18)",
          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        }}>
          {icon}
        </Box>
        <Box minWidth={0}>
          <Typography sx={{
            fontSize: 9.5, fontWeight: 700, color: "rgba(255,255,255,0.85)",
            textTransform: "uppercase", letterSpacing: 0.5,
          }}>
            {label}
          </Typography>
          <Typography sx={{ fontSize: { xs: 18, sm: 22 }, fontWeight: 800, color: "#fff", lineHeight: 1 }}>
            {value}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", bgcolor: C.bg }}>
      <TopBar />

      <Box sx={{ flex: 1 }}>

        {/* ── Hero header ──────────────────────────────────────── */}
        <Box sx={{ px: { xs: 1.5, sm: 3, md: 4.75 }, pt: { xs: 2.5, sm: 3 } }}>
          <Paper
            elevation={0}
            sx={{
              position: "relative",
              borderRadius: 3, overflow: "hidden",
              background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryDark} 100%)`,
              p: { xs: 3, sm: 4 },
              mb: { xs: 2.5, sm: 3 },
              color: "#fff",
              boxShadow: `0 6px 24px ${C.primary}30`,
            }}
          >
            <Box sx={{
              position: "absolute",
              top: "-40%", right: "-10%",
              width: { xs: 200, sm: 280 }, height: { xs: 200, sm: 280 },
              borderRadius: "50%",
              background: `radial-gradient(circle, ${C.secondary}40 0%, transparent 70%)`,
              pointerEvents: "none",
            }} />

            <Box sx={{ position: "relative", zIndex: 1 }}>
              <Typography sx={{
                fontSize: { xs: 11, sm: 12 }, fontWeight: 700, letterSpacing: 1.5,
                textTransform: "uppercase", color: "rgba(255,255,255,0.7)", mb: 0.75,
              }}>
                Curriculum
              </Typography>
              <Typography sx={{
                fontWeight: 800, fontSize: { xs: 22, sm: 28, md: 36 },
                color: "#fff", lineHeight: 1.15, mb: 0.75, letterSpacing: -0.5,
              }}>
                {firstName ? `${firstName}, keep learning` : "Lessons"}
              </Typography>
              <Typography sx={{
                color: "rgba(255,255,255,0.85)",
                fontSize: { xs: 13, sm: 14 }, maxWidth: 520, lineHeight: 1.5,
              }}>
                Explore lessons, track your progress, and earn certificates as you go
              </Typography>
            </Box>
          </Paper>

          {/* Stat row — only if learner has any progress */}
          {learnerId && totalLessons > 0 && (
            <Stack
              direction="row"
              spacing={{ xs: 1, sm: 1.5 }}
              mb={{ xs: 2.5, sm: 3 }}
              sx={{ flexWrap: { xs: "wrap", sm: "nowrap" }, gap: { xs: 1, sm: 0 } }}
            >
              <StatTile label="Available" value={totalLessons}
                accent={C.primary} accentDark={C.primaryDark}
                icon={<MenuBook sx={{ fontSize: 16, color: "#fff" }} />} />
              {/* <StatTile label="In Progress" value={inProgressCount}
                accent={C.primaryLight} accentDark={C.primary}
                icon={<TimelapseOutlined sx={{ fontSize: 16, color: "#fff" }} />} /> */}
              <StatTile label="Completed" value={completedCount}
                accent={C.secondary} accentDark={C.secondaryDark}
                icon={<EmojiEvents sx={{ fontSize: 16, color: "#fff" }} />} />
            </Stack>
          )}
        </Box>

        {/* ── Main layout ──────────────────────────────────────── */}
        <Box sx={{
          px: { xs: 1.5, sm: 3, md: 4.75 },
          pb: { xs: 4, sm: 6 },
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: { xs: 2, md: 3 },
          alignItems: "flex-start",
          width: "100%",
          boxSizing: "border-box",
        }}>

          {/* Desktop sidebar filter */}
          {!isMobile && (
            <Paper
              elevation={0}
              sx={{
                flexShrink: 0,
                width: { md: 280, lg: 300 },
                bgcolor: C.paper,
                borderRadius: 3,
                border: `1px solid ${C.border}`,
                p: 2.5,
                boxShadow: "0 2px 12px rgba(13,17,23,0.04)",
                position: "sticky",
                top: 16,
              }}
            >
              <FilterPanel />
            </Paper>
          )}

          {/* Lessons list */}
          <Paper
            elevation={0}
            sx={{
              flex: 1, minWidth: 0, width: "100%", boxSizing: "border-box",
              bgcolor: C.paper, borderRadius: 3,
              border: `1px solid ${C.border}`,
              p: { xs: 2, sm: 2.5, md: 3 },
              boxShadow: "0 2px 12px rgba(13,17,23,0.04)",
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
              <Typography sx={{ color: C.textPrimary, fontSize: { xs: 15, sm: 16 }, fontWeight: 700, letterSpacing: -0.2 }}>
                All Lessons
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography sx={{ color: C.textMuted, fontSize: { xs: 11.5, sm: 12.5 } }}>
                  {sortedLessons.length} found
                </Typography>
                {isMobile && (
                  <Button
                    size="small"
                    startIcon={<FilterList sx={{ fontSize: 16 }} />}
                    onClick={() => setFilterDrawerOpen(true)}
                    sx={{
                      bgcolor: filtersActive ? C.primary : C.primarySoft,
                      color: filtersActive ? "#fff" : C.primary,
                      textTransform: "none", fontWeight: 700, fontSize: 12,
                      borderRadius: 2, px: 1.5, py: 0.5,
                      "&:hover": { bgcolor: filtersActive ? C.primaryDark : alpha(C.primary, 0.15) },
                    }}
                  >
                    Filters
                    {filtersActive && (
                      <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#fff", ml: 0.75 }} />
                    )}
                  </Button>
                )}
              </Stack>
            </Stack>

            <Divider sx={{ mb: 2, borderColor: C.borderSoft }} />

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress sx={{ color: C.primary }} />
              </Box>
            ) : error ? (
              <Box sx={{
                p: 2, borderRadius: 2,
                bgcolor: C.errorSoft, color: C.error,
                fontSize: 13, fontWeight: 600,
              }}>
                {error}
              </Box>
            ) : currentLessons.length === 0 ? (
              <Stack alignItems="center" spacing={1} py={6}>
                <MenuBook sx={{ fontSize: 44, color: C.textMuted, opacity: 0.5 }} />
                <Typography sx={{ color: C.textPrimary, fontWeight: 700, fontSize: 14 }}>
                  No lessons found
                </Typography>
                <Typography sx={{ color: C.textSecondary, fontSize: 12, textAlign: "center", maxWidth: 280 }}>
                  Try adjusting your filters or clearing them to see all available lessons
                </Typography>
              </Stack>
            ) : (
              <>
                {/* Lesson cards */}
                <Stack spacing={2}>
                  {currentLessons.map((lesson) => {
                    const progress = progressData[lesson._id];
                    const status = computeLessonStatus(progress);
                    const sc = STATUS_CONFIG[status];
                    const completionPct = progress?.completionPercentage || 0;
                    const hasCertificate = progress?.certificateIssued || false;
                    const sectionCount = lesson.sections?.length || 0;
                    const dc = DIFFICULTY_COLORS[lesson.difficulty] || { color: C.primary, bg: C.primarySoft };
                    const isCompleted = status === "completed";

                    return (
                      <Card
                        key={lesson._id}
                        elevation={0}
                        sx={{
                          position: "relative",
                          borderRadius: 2.5,
                          border: `1px solid ${C.border}`,
                          bgcolor: isCompleted ? C.secondarySofter : C.paper,
                          p: { xs: 1.75, sm: 2.25 },
                          width: "100%", boxSizing: "border-box",
                          transition: "all 0.2s ease",
                          overflow: "hidden",
                          "&:hover": {
                            borderColor: sc.accent,
                            boxShadow: `0 6px 20px ${sc.accent}15`,
                            transform: "translateY(-2px)",
                          },
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            top: 0, left: 0, right: 0,
                            height: "3px",
                            background: `linear-gradient(90deg, ${sc.accent}, ${sc.accent}88)`,
                          },
                        }}
                      >
                        {/* Card header — icon + title + status */}
                        <Stack direction="row" alignItems="flex-start" spacing={1.5} mb={1.5}>
                          <Box sx={{
                            width: 42, height: 42, borderRadius: 1.75,
                            flexShrink: 0,
                            background: isCompleted
                              ? `linear-gradient(135deg, ${C.secondary}, ${C.secondaryLight})`
                              : `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            boxShadow: `0 3px 8px ${sc.accent}25`,
                          }}>
                            {isCompleted
                              ? <EmojiEvents sx={{ fontSize: 22, color: "#fff" }} />
                              : <PlayCircle sx={{ fontSize: 22, color: "#fff" }} />}
                          </Box>

                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{
                              fontSize: { xs: 13.5, sm: 15 }, fontWeight: 700,
                              color: C.textPrimary,
                              mb: 0.5, lineHeight: 1.35,
                              textDecoration: isCompleted ? "line-through" : "none",
                              textDecorationColor: alpha(C.textMuted, 0.5),
                            }}>
                              {lesson.title}
                            </Typography>

                            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                              {/* Status chip — the key piece */}
                              <Chip
                                icon={sc.icon}
                                label={sc.label}
                                size="small"
                                sx={{
                                  height: 22, fontSize: 11, fontWeight: 700,
                                  bgcolor: sc.bg, color: sc.color,
                                  "& .MuiChip-label": { px: 0.75 },
                                  "& .MuiChip-icon": { color: sc.color },
                                }}
                              />
                              {lesson.category && (
                                <Chip label={lesson.category} size="small"
                                  sx={{
                                    height: 22, fontSize: 11, fontWeight: 600,
                                    bgcolor: C.primarySoft, color: C.primary,
                                    textTransform: "capitalize",
                                    "& .MuiChip-label": { px: 0.75 },
                                  }} />
                              )}
                              {lesson.difficulty && (
                                <Chip label={lesson.difficulty} size="small"
                                  sx={{
                                    height: 22, fontSize: 11, fontWeight: 600,
                                    bgcolor: dc.bg, color: dc.color,
                                    textTransform: "capitalize",
                                    "& .MuiChip-label": { px: 0.75 },
                                  }} />
                              )}
                              {hasCertificate && (
                                <Chip icon={<EmojiEvents sx={{ fontSize: "13px !important", color: `${C.warning} !important` }} />}
                                  label="Certified" size="small"
                                  sx={{
                                    height: 22, fontSize: 11, fontWeight: 700,
                                    bgcolor: C.warningSoft, color: C.warning,
                                    "& .MuiChip-label": { px: 0.75 },
                                  }} />
                              )}
                            </Stack>
                          </Box>
                        </Stack>

                        {/* Description */}
                        <Typography sx={{
                          color: C.textSecondary,
                          fontSize: { xs: 12, sm: 13 },
                          mb: 1.5, lineHeight: 1.6,
                          overflow: "hidden", display: "-webkit-box",
                          WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                        }}>
                          {lesson.description}
                        </Typography>

                        {/* Meta info */}
                        <Stack direction="row" spacing={1.5} mb={1.5} flexWrap="wrap" useFlexGap>
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <MenuBook sx={{ fontSize: 14, color: C.textMuted }} />
                            <Typography sx={{ fontSize: 11.5, color: C.textMuted, fontWeight: 600 }}>
                              {sectionCount} {sectionCount === 1 ? "Section" : "Sections"}
                            </Typography>
                          </Stack>
                          {lesson.totalDuration > 0 && (
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <AccessTime sx={{ fontSize: 14, color: C.textMuted }} />
                              <Typography sx={{ fontSize: 11.5, color: C.textMuted, fontWeight: 600 }}>
                                {lesson.totalDuration} mins
                              </Typography>
                            </Stack>
                          )}
                        </Stack>

                        {/* Progress bar — only when started */}
                        {progress && completionPct > 0 && (
                          <Box mb={1.75}>
                            <Stack direction="row" justifyContent="space-between" mb={0.5}>
                              <Typography sx={{
                                fontSize: 10, color: C.textMuted, fontWeight: 700,
                                textTransform: "uppercase", letterSpacing: 0.5,
                              }}>
                                Progress
                              </Typography>
                              <Typography sx={{
                                fontSize: 11, fontWeight: 700,
                                color: isCompleted ? C.secondary : C.primary,
                              }}>
                                {completionPct}%
                              </Typography>
                            </Stack>
                            <LinearProgress
                              variant="determinate" value={completionPct}
                              sx={{
                                height: 6, borderRadius: 3, bgcolor: C.borderSoft,
                                "& .MuiLinearProgress-bar": {
                                  background: isCompleted
                                    ? `linear-gradient(90deg, ${C.secondary}, ${C.secondaryLight})`
                                    : `linear-gradient(90deg, ${C.primary}, ${C.primaryLight})`,
                                  borderRadius: 3,
                                },
                              }}
                            />
                          </Box>
                        )}

                        {/* CTA */}
                        <Button
                          fullWidth
                          variant="contained"
                          onClick={() => navigate(`/lesson/${lesson._id}`)}
                          startIcon={
                            status === "completed" ? <CheckCircle sx={{ fontSize: 16 }} />
                            : status === "in_progress" ? <TimelapseOutlined sx={{ fontSize: 16 }} />
                            : <School sx={{ fontSize: 16 }} />
                          }
                          sx={{
                            bgcolor: sc.accent,
                            color: "#fff",
                            borderRadius: 2,
                            textTransform: "none",
                            fontSize: 12.5, fontWeight: 700,
                            py: 0.85, boxShadow: "none",
                            "&:hover": {
                              bgcolor: status === "completed" ? C.secondaryDark : C.primaryDark,
                              boxShadow: `0 4px 12px ${sc.accent}30`,
                            },
                          }}
                        >
                          {sc.buttonLabel}
                        </Button>
                      </Card>
                    );
                  })}
                </Stack>

                {/* Pagination */}
                {totalPages > 1 && (
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mt={3} pt={2.5}
                    sx={{ borderTop: `1px solid ${C.borderSoft}` }}>
                    <Button
                      variant="outlined" disabled={currentPage === 1} onClick={prevPage}
                      sx={{
                        borderRadius: 2, borderColor: C.primary, color: C.primary,
                        fontWeight: 700, fontSize: 12.5, textTransform: "none", px: 2.5,
                        "&:hover": { borderColor: C.primaryDark, bgcolor: C.primarySoft },
                        "&.Mui-disabled": { borderColor: C.borderSoft, color: C.textMuted },
                      }}
                    >
                      ← Previous
                    </Button>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      {Array.from({ length: totalPages }, (_, i) => (
                        <Box
                          key={i}
                          onClick={() => setCurrentPage(i + 1)}
                          sx={{
                            width: i + 1 === currentPage ? 24 : 8, height: 8,
                            borderRadius: 4,
                            bgcolor: i + 1 === currentPage ? C.primary : alpha(C.primary, 0.2),
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                          }}
                        />
                      ))}
                    </Stack>
                    <Button
                      variant="outlined" disabled={currentPage === totalPages} onClick={nextPage}
                      sx={{
                        borderRadius: 2, borderColor: C.primary, color: C.primary,
                        fontWeight: 700, fontSize: 12.5, textTransform: "none", px: 2.5,
                        "&:hover": { borderColor: C.primaryDark, bgcolor: C.primarySoft },
                        "&.Mui-disabled": { borderColor: C.borderSoft, color: C.textMuted },
                      }}
                    >
                      Next →
                    </Button>
                  </Stack>
                )}
              </>
            )}
          </Paper>
        </Box>

        <Footer />
      </Box>

      {/* Mobile filter drawer */}
      {isMobile && (
        <Drawer
          anchor="bottom"
          open={filterDrawerOpen}
          onClose={() => setFilterDrawerOpen(false)}
          PaperProps={{
            sx: {
              borderTopLeftRadius: 20, borderTopRightRadius: 20,
              maxHeight: "85vh", overflowY: "auto",
              px: 2, pt: 2, pb: 4,
            },
          }}
        >
          <Box sx={{ width: 40, height: 4, bgcolor: C.borderSoft, borderRadius: 2, mx: "auto", mb: 2 }} />
          <FilterPanel />
        </Drawer>
      )}
    </Box>
  );
}